<template>
  <BaseModal no-exit no-padding no-toolbar card-width="600px" @close="$emit('close')">
    <template #default>
      <div class="w-full flex flex-col">
        <!-- Header -->
        <div class="relative w-full flex items-center gap-2 p-5 border-b border-border-normal">
          <CreateSavedFilterIcon class="text-icon-normal" :width="20" :height="20" />
          <BaseText type="label" size="sm" class="text-text-muted">
            {{ initFilterPreset ? `Edit '${initFilterPreset.name}'` : 'Create New Filter Preset' }}
          </BaseText>
          <button class="absolute top-3 right-3 p-1" @click="$emit('close')">
            <TagRemoveIcon stroke="#5E6678" :width="16" :height="16" />
          </button>
        </div>
        <!-- Body -->
        <div class="w-full flex flex-col p-5">
          <BaseText type="label" size="sm" class="text-text-muted">
            Preset Name
          </BaseText>
          <div class="name-input-container mt-1 px-2 py-1.5">
            <input class="name-input" v-model="filterPresetName" placeholder="Enter preset name" maxLength="30" />
            <BaseText type="body" size="sm" class="text-text-subdued justify-self-end">
              {{ filterPresetName.length }}/30
            </BaseText>
          </div>
          <!-- Select Filters -->
          <div class="w-full flex gap-2 mt-5">
            <!-- Add Filter Button -->
            <div class="relative flex-shrink-0 self-start" v-on-clickaway="() => { filterDropdownExpanded = false }">
              <button class="flex items-center p-1.5 rounded-md transition-colors duration-100"
              :class="filterDropdownExpanded ? 'bg-neutral-25' : 'hover:bg-neutral-25'"
              @click="filterDropdownExpanded = !filterDropdownExpanded">
                <FilterIcon class="text-icon-normal" />
                <BaseText type="label" size="sm" class="px-1.5 text-text-muted">
                  Add Filter
                </BaseText>
              </button>
              <transition>
                <div v-if="filterDropdownExpanded" class="filter-select-dropdown">
                  <FilterSelectDropdown
                    hide-saved-filters
                    :active-filters="presetFilters"
                    :allowed-filter-types="allowedFilterTypes"
                    :excluded-filter-types="excludedFilterTypes"
                    @applyFilters="applyFilterChanges($event)"
                    @close="filterDropdownExpanded = false"
                  />
                </div>
              </transition>
            </div>
            <!-- Applied Preset Filters -->
            <ActiveFiltersList 
              :active-filters="presetFilters" 
              is-preset-editor
              @applyFilters="applyFilterChanges($event)"
            />
          </div>
        </div>
        <!-- Footer -->
        <div class="w-full flex items-center justify-between p-5 border-t border-border-normal">
          <button class="px-2.5 py-1.5 rounded-md transition-colors duration-100 hover:bg-neutral-25"
          @click="$emit('close')">
            <BaseText type="label" size="sm" class="text-text-muted">
              Cancel
            </BaseText>
          </button>
          <button class="submit-btn px-3 py-1.5 rounded-md flex justify-center" :class="submitEnabled ? 'enabled' : 'disabled'"
          :style="{ width: submitWidth }" :disabled="!submitEnabled" @click="submitPreset">
            <BaseLoadingSpinnerCircle v-if="submitLoading" :duration="1" />
            <BaseText v-else type="label" size="sm">
              {{ initFilterPreset ? 'Update Preset' : 'Create Preset' }}
            </BaseText>
          </button>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { mixin as clickaway } from 'vue-clickaway2'
import FirebaseAPI from '@/api/firebase'
import posthog from 'posthog-js'

// Components
import FilterSelectDropdown from './FilterSelectDropdown.vue'
import ActiveFiltersList from './ActiveFiltersList.vue'

// Icons
import CreateSavedFilterIcon from '../globals/Icons/FilterIcons/CreateSavedFilterIcon.vue'
import TagRemoveIcon from '../globals/Icons/TagRemoveIcon.vue'
import FilterIcon from '../globals/Icons/FilterIcons/FilterIcon.vue'

export default {
  name: 'FilterPresetModal',
  mixins: [clickaway],
  components: {
    FilterSelectDropdown,
    ActiveFiltersList,
    CreateSavedFilterIcon,
    TagRemoveIcon,
    FilterIcon
  },
  props: {
    // If we are editing an existing preset
    initFilterPreset: {
      type: Object,
      default: () => null
    },
    // If we're creating a new preset with initial filters
    initFilters: {
      type: Object,
      default: () => ({})
    },
    allowedFilterTypes: {
      type: Array,
      default: () => []
    },
    excludedFilterTypes: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      filterPresetName: this.initFilterPreset?.name ?? '',
      presetFilters: this.initFilterPreset?.filters ?? this.initFilters ?? {},
      filterDropdownExpanded: false,
      submitLoading: false
    }
  },
  mounted () {
    this.$nextTick(() => {
      switch (this.getTheme) {
        case 'blue': this.$el.style.setProperty('--button-color', '#1F69FF'); break;
        case 'green': this.$el.style.setProperty('--button-color', '#009F6D'); break;
        case 'purple': this.$el.style.setProperty('--button-color', '#6534DF'); break;
        case 'red': this.$el.style.setProperty('--button-color', '#FF5453'); break;
        case 'black': this.$el.style.setProperty('--button-color', '#121212'); break;
      }
    })
  },
  computed: {
    ...mapGetters('MiscModule', ['getTheme']),
    submitEnabled () {
      return this.filterPresetName.length > 0 
        && Object.keys(this.presetFilters).length > 0 
        && !this.submitLoading
        && this.isPresetChanged
    },
    isPresetChanged () {
      return !this.initFilterPreset 
        || this.initFilterPreset?.name !== this.filterPresetName 
        || !_.isEqual(this.initFilterPreset?.filters, this.presetFilters)
    },
    submitWidth () {
      return this.initFilterPreset ? '120px' : '116px'
    }
  },
  methods: {
    ...mapActions('SearchModule', ['fetchSavedFilters']),
    applyFilterChanges (filters) {
      this.presetFilters = { ...filters }
    },
    async submitPreset () {
      this.submitLoading = true
      try {
        if (this.initFilterPreset) {
          // Edit Mode
          const updatedPreset = {
            ...this.initFilterPreset,
            name: this.filterPresetName.trim(),
            filters: this.presetFilters
          }
          await FirebaseAPI.Filters.updateSavedFilter(this.initFilterPreset.id, updatedPreset)
          await this.fetchSavedFilters()
          this.$showAlert({
            type: 'success',
            message: 'Preset updated successfully'
          })
        } else {
          // Create Mode
          const newPreset = {
            name: this.filterPresetName.trim(),
            filters: this.presetFilters,
            product: this.$route.meta.product
          }
          await FirebaseAPI.Filters.createSavedFilter(newPreset)
          await this.fetchSavedFilters()

          // PostHog tracking
          posthog.capture('Saved Filter', {
            filter_name: newPreset.name,
            product: newPreset.product,
            filter_count: newPreset.filters.length
          })

          this.$showAlert({
            type: 'success',
            message: 'Preset created successfully'
          })
        }
        this.$emit('close')
      } catch (error) {
        console.error(error)
        this.$showAlert({ 
          type: 'error', 
          message: 'Error occurred while submitting preset. See console for details.' 
        })
      } finally {
        this.submitLoading = false
      }
    }
  }
}
</script>

<style scoped>
.filter-select-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 500;
}

.name-input-container {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 40px;
  border-radius: 6px;
  box-shadow: 0 1px 2px 0 rgba(0, 56, 108, 0.08), 0 0 0 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 150ms ease-in-out;
}
.name-input-container:focus-within {
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.1), 0px 0px 0px 1px rgba(0, 56, 108, 0.1);
}
.name-input {
  color: #06070F;
  
  /* Body/Small */
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
}
.name-input:focus {
  outline: none;
}
.name-input::placeholder {
  color: #5E6678;
  transition: color 150ms ease-in-out;
  opacity: 0.9;
}
.name-input-container:hover .name-input::placeholder {
  color: #303546;
}
.name-input:focus::placeholder, .name-input-container:hover .name-input:focus::placeholder {
  color: #C1C7D0;
}

@property --submit-btn-opacity-1 {
syntax: '<number>';
initial-value: 0.12;
inherits: false;
}
@property --submit-btn-opacity-2 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
@property --submit-color {
  syntax: '<color>';
  inherits: false;
}
.submit-btn {
  --submit-color: var(--button-color);
  background: linear-gradient(180deg, rgba(255, 255, 255, var(--submit-btn-opacity-1)) 0%, rgba(255, 255, 255, var(--submit-btn-opacity-2)) 100%), var(--submit-color);
  color: white;
  box-shadow: 0px -1px 12px 0px rgba(255, 255, 255, 0.12) inset, 0px 0px 0px 1px var(--submit-color);
  transition: box-shadow 100ms ease-in-out,
    color 100ms ease-in-out, 
    background-color 100ms ease-in-out,
    --submit-btn-opacity-1 100ms ease-in-out, 
    --submit-btn-opacity-2 100ms ease-in-out,
    --submit-color 100ms ease-in-out;
}
.submit-btn.enabled:hover {
  --submit-btn-opacity-1: 0.24;
  --submit-btn-opacity-2: 0;
}
.submit-btn.disabled {
  color: #A4ABB8;
  box-shadow: none;
  --submit-btn-opacity-1: 0;
  --submit-btn-opacity-2: 0;
  --submit-color: #F6F8FA;
}

.v-enter-active, .v-leave-active {
  transition: opacity 150ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
</style>